import { useEffect, useState } from 'react';
import { doc, setDoc, deleteDoc, onSnapshot, collection } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { usePet } from '../context/PetContext';

export interface ActiveCarer {
  uid: string;
  display_name: string;
  last_seen: string; // ISO
}

/**
 * Writes a presence doc at:
 *   users/{ownerUid}/pets/{petId}/activity/{uid}
 *
 * On mount: sets active=true + last_seen
 * On unmount / page hide: deletes the doc (clears presence)
 *
 * Also subscribes to presence docs from other co-carers and exposes them
 * so Layout.tsx can show "Sarah is also caring for Koda right now".
 *
 * A carer is considered "active" if their last_seen is within 5 minutes.
 */
export function usePetPresence() {
  const { user } = useAuth();
  const { activePetId, activePetOwnerUid } = usePet();
  const [otherActiveCarers, setOtherActiveCarers] = useState<ActiveCarer[]>([]);

  // Write own presence and clear on unmount
  useEffect(() => {
    if (!user?.id || !activePetId || !activePetOwnerUid) return;

    const petBase = `users/${activePetOwnerUid}/pets/${activePetId}`;
    const presenceRef = doc(db, petBase, 'activity', user.id);

    const writePresence = () => {
      setDoc(presenceRef, {
        uid: user.id,
        display_name: user.full_name || 'A carer',
        last_seen: new Date().toISOString(),
        active: true,
      }).catch(() => {}); // best-effort
    };

    const clearPresence = () => {
      deleteDoc(presenceRef).catch(() => {});
    };

    writePresence();

    // Refresh every 2 minutes to keep presence alive
    const interval = setInterval(writePresence, 2 * 60 * 1000);

    // Clear on tab hide or close
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') clearPresence();
      else writePresence();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearPresence();
    };
  }, [user?.id, user?.full_name, activePetId, activePetOwnerUid]);

  // Subscribe to other co-carers' presence
  useEffect(() => {
    if (!user?.id || !activePetId || !activePetOwnerUid) return;

    const petBase = `users/${activePetOwnerUid}/pets/${activePetId}`;
    const unsub = onSnapshot(
      collection(db, petBase, 'activity'),
      snap => {
        const FIVE_MINUTES = 5 * 60 * 1000;
        const now = Date.now();
        const others = snap.docs
          .map(d => d.data() as ActiveCarer & { active?: boolean })
          .filter(c => {
            if (c.uid === user.id) return false; // skip self
            const age = now - new Date(c.last_seen).getTime();
            return age < FIVE_MINUTES;
          });
        setOtherActiveCarers(others);
      },
      () => {} // best-effort
    );

    return unsub;
  }, [user?.id, activePetId, activePetOwnerUid]);

  return { otherActiveCarers };
}
